import { Globals } from '../globalfunctions.js';

class MFAEnforcer {
  
  is2FARequired() {
    // 2FA is now mandatory for all users
    return true;
  }
  
  is2FASetupComplete() {
    const has2FA = localStorage.getItem('2faEnabled');
    const setupComplete = localStorage.getItem('2faCompleted');
    
    // Handle multiple methods case
    if (has2FA === 'multiple') {
      return !!setupComplete;
    }
    
    return !!(has2FA && setupComplete);
  }
  
  getEnabled2FAMethods() {
    const enabled = localStorage.getItem('2faEnabled');
    if (enabled === 'multiple') {
      // For multiple methods, we'd need to fetch from server
      // For now, return both as potentially available
      return ['totp', 'webauthn'];
    }
    return enabled ? [enabled] : [];
  }
  
  is2FASessionValid() {
    const sessionValid = localStorage.getItem('2faSessionValid');
    const sessionTime = localStorage.getItem('2faSessionTime');
    
    if (!sessionValid || !sessionTime) {
      return false;
    }
    
    // 2FA session is valid for 12 hours
    const maxSessionTime = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
    const currentTime = new Date().getTime();
    const loginTime = parseInt(sessionTime);
    
    return (currentTime - loginTime) < maxSessionTime;
  }
  
  markSessionAs2FAVerified() {
    localStorage.setItem('2faSessionValid', 'true');
    localStorage.setItem('2faSessionTime', new Date().getTime().toString());
  }
  
  clear2FASession() {
    localStorage.removeItem('2faSessionValid');
    localStorage.removeItem('2faSessionTime');
  }
  
  canAccessApplication() {
    // User must be logged in
    if (!Globals.LoginTime) {
      return { allowed: false, reason: 'not_logged_in' };
    }
    
    // User must have 2FA setup
    if (!this.is2FASetupComplete()) {
      return { allowed: false, reason: '2fa_not_setup' };
    }
    
    // User must have valid 2FA session
    if (!this.is2FASessionValid()) {
      return { allowed: false, reason: '2fa_session_expired' };
    }
    
    return { allowed: true };
  }
  
  getRedirectPath(reason) {
    switch (reason) {
      case 'not_logged_in':
        return '/login';
      case '2fa_not_setup':
        return '/setup-2fa';
      case '2fa_session_expired':
        return '/verify-2fa';
      default:
        return '/login';
    }
  }
  
  requiresPeriodicReauth() {
    // Require re-authentication every 12 hours
    const sessionTime = localStorage.getItem('2faSessionTime');
    if (!sessionTime) return true;
    
    const reauthInterval = 12 * 60 * 60 * 1000; // 12 hours
    const currentTime = new Date().getTime();
    const loginTime = parseInt(sessionTime);
    
    return (currentTime - loginTime) > reauthInterval;
  }
  
  getSessionTimeRemaining() {
    const sessionTime = localStorage.getItem('2faSessionTime');
    if (!sessionTime) return 0;
    
    const maxSessionTime = 12 * 60 * 60 * 1000; // 12 hours
    const currentTime = new Date().getTime();
    const loginTime = parseInt(sessionTime);
    const elapsed = currentTime - loginTime;
    return Math.max(0, maxSessionTime - elapsed);
  }
  
  formatTimeRemaining(milliseconds) {
    if (milliseconds <= 0) return '0 minutes';
    
    const hours = Math.floor(milliseconds / (60 * 60 * 1000));
    const minutes = Math.floor((milliseconds % (60 * 60 * 1000)) / (60 * 1000));
    
    if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} and ${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
    
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
  
  // Method to check if user needs to re-authenticate for sensitive operations
  requiresElevatedAuth() {
    const lastElevatedAuth = localStorage.getItem('lastElevatedAuth');
    if (!lastElevatedAuth) return true;
    
    // Require elevated auth every 30 minutes for sensitive operations
    const elevatedAuthInterval = 30 * 60 * 1000; // 30 minutes
    const currentTime = new Date().getTime();
    const lastAuthTime = parseInt(lastElevatedAuth);
    
    return (currentTime - lastAuthTime) > elevatedAuthInterval;
  }
  
  markElevatedAuth() {
    localStorage.setItem('lastElevatedAuth', new Date().getTime().toString());
  }
}

const mfaEnforcer = new MFAEnforcer();
export default mfaEnforcer;